import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import Dropdown from '../../components/Dropdown';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const TakeActionModal = ({ lead, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [actionOutcome, setActionOutcome] = useState('Completed');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionDate, setActionDate] = useState(null);
  const [actionTime, setActionTime] = useState('');
  const [actionDaysToAdd, setActionDaysToAdd] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [nextStep, setNextStep] = useState('Complete');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [executiveSchedule, setExecutiveSchedule] = useState({ followups: [], deliveries: [] });
  const [isFetchingSchedule, setIsFetchingSchedule] = useState(false);
  const nextStepOptions = [
    { value: 'Schedule', label: 'Schedule' },
    { value: 'On Hold', label: 'On Hold' },
    { value: 'Reschedule', label: 'Reschedule' },
    { value: 'Complete', label: 'Complete' },
    { value: 'Lost', label: 'Lost' }
  ];


  useEffect(() => {
    api.get('/masters/status').then(res => {
      setStatuses(res.data.filter(s => s.status === 'Active'));
    }).catch(console.error);
  }, []);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!actionDate || !lead) {
        setExecutiveSchedule({ followups: [], deliveries: [] });
        return;
      }
      console.log("LEAD OBJ IN TAKE ACTION", lead);
      const assigneeId = lead.userId || (lead.user && lead.user.id);
      console.log("ASSIGNEE ID", assigneeId);
      if (!assigneeId) return;

      setIsFetchingSchedule(true);
      try {
        const d = new Date(actionDate);
        const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const res = await api.get(`/users/${assigneeId}/schedule?date=${dateString}&_t=${Date.now()}`);
        setExecutiveSchedule(res.data);
      } catch (err) {
        console.error('Failed to fetch schedule', err);
        setExecutiveSchedule({ followups: [], deliveries: [] });
      } finally {
        setIsFetchingSchedule(false);
      }
    };
    fetchSchedule();
  }, [actionDate, lead]);

  const isStarted = React.useMemo(() => {
    if (!lead?.followups || lead.followups.length === 0) return false;
    const sorted = [...lead.followups].sort((a, b) => new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime());
    const latestVisitAction = sorted.find(f => {
      const n = f.notes || '';
      if (n.startsWith('[Closed]') || n.startsWith('[Completed]') || n.startsWith('[Cancelled]')) return false;
      const t = f.type || '';
      const dt = f.displayTitle || '';
      return t === 'Visit Started' || dt === 'Visit Started' || n.startsWith('Visit Started') || n.includes('[Start Visit]') ||
        t === 'Visit Completed' || dt === 'Visit Completed' || n.includes('[Complete Visit]') ||
        t === 'Visit Cancelled' || dt === 'Visit Cancelled' || n.includes('[Cancel Visit]');
    });

    if (latestVisitAction) {
      const t = latestVisitAction.type || '';
      const dt = latestVisitAction.displayTitle || '';
      const n = latestVisitAction.notes || '';
      if (!n.startsWith('[Closed]') && (t === 'Visit Started' || dt === 'Visit Started' || n.startsWith('Visit Started') || n.includes('[Start Visit]'))) {
        return true;
      }
    }
    return false;
  }, [lead]);

  const dropdownOptions = React.useMemo(() => {
    let opts = statuses.map(s => ({ value: s.name, label: s.name }));
    if (isStarted) {
      opts = [
        { value: 'Complete Visit', label: 'Complete Visit' },
        { value: 'Cancel Visit', label: 'Cancel Visit' },
      ];
    } else {
      const statusStr = typeof lead?.status === 'string' ? lead.status : (lead?.status?.name || '');
      const isScheduleStatus = statusStr.toLowerCase() === 'schedule' ||
        statusStr.toLowerCase() === 'reschedule';
      if (isScheduleStatus) {
        opts = [
          { value: 'Start Visit', label: 'Start Visit' },
          { value: 'Cancel Visit', label: 'Cancel Visit' },
        ];
      }
    }
    return opts;
  }, [statuses, lead, isStarted]);

  useEffect(() => {
    if (dropdownOptions.length > 0) {
      if (!dropdownOptions.find(o => o.value === actionOutcome)) {
        setActionOutcome(dropdownOptions[0].value);
      }
    }
  }, [dropdownOptions, actionOutcome]);

  const handleActionDaysChange = (val) => {
    setActionDaysToAdd(val);
    if (val && !isNaN(val)) {
      const d = new Date();
      d.setDate(d.getDate() + parseInt(val, 10));
      setActionDate(d);
    } else {
      setActionDate(null);
    }
  };

  const handleActionDateChange = (date) => {
    setActionDate(date);
    if (date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sel = new Date(date);
      sel.setHours(0, 0, 0, 0);
      const diffDays = Math.round((sel - today) / (1000 * 60 * 60 * 24));
      setActionDaysToAdd(diffDays >= 0 ? diffDays.toString() : '0');
    } else {
      setActionDaysToAdd('');
    }
  };

  const getTimeDate = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':');
    const d = new Date();
    d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
    return d;
  };

  const getFollowupType = (outcome, nextStep) => {
    if (!outcome) return 'Activity';
    if (outcome === 'Start Visit') return 'Visit Started';
    if (outcome === 'Complete Visit') return 'Visit Completed';
    if (outcome === 'Cancel Visit') return 'Follow-up Cancelled';
    if (outcome.toLowerCase().includes('reschedule') || (nextStep || '').toLowerCase() === 'reschedule') return 'Follow-up Rescheduled';
    if (outcome.toLowerCase().includes('schedule') || (nextStep || '').toLowerCase() === 'schedule') return 'Follow-up Scheduled';
    if (['won', 'completed', 'complete'].includes(outcome.toLowerCase())) return 'Lead is Complete';
    return outcome;
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!lead) {
      setIsSubmitting(false);
      return;
    }

    const requiresDate = actionOutcome?.toLowerCase()?.includes('schedule') || actionOutcome?.toLowerCase()?.includes('follow') || ((actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') && (nextStep === 'Reschedule' || nextStep?.toLowerCase()?.includes('schedule')));
    if (requiresDate && (!actionDate || !actionTime)) {
      toast.error("Please select a date and time for the follow-up");
      setIsSubmitting(false);
      return;
    }

    try {
      let targetFollowup = null;
      let isTerminal = ['complete', 'completed', 'closed', 'lost', 'complete visit', 'cancel visit'].includes(actionOutcome?.toLowerCase());
      if ((actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') && (nextStep === 'Reschedule' || nextStep?.toLowerCase()?.includes('schedule'))) isTerminal = false; // reschedule/schedule is not terminal


      if (lead.followups && lead.followups.length > 0) {
        const valid = lead.followups.filter(f => f.type !== 'System' && (!/^\[.*?\]/.test(f.notes || '') || f.notes?.startsWith('[Reschedule]')));
        if (valid.length > 0) {
          valid.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          targetFollowup = valid[0];
        } else {
          const nonSystem = lead.followups.filter(f => f.type !== 'System');
          if (nonSystem.length > 0) {
            nonSystem.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            targetFollowup = nonSystem[0];
          }
        }
      }

      if (targetFollowup) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const scheduledDate = new Date(targetFollowup.date);
        scheduledDate.setHours(0, 0, 0, 0);
        const isEarly = today < scheduledDate;

        // Close the original log without changing its date or type to preserve history
        await api.put(`/followups/${targetFollowup.id}`, {
          notes: targetFollowup.notes?.startsWith('[Closed]') ? targetFollowup.notes : `[Closed] ${targetFollowup.notes || ''}`.trim(),
          type: targetFollowup.type,
          date: targetFollowup.date
        });

        let newNotes = `[${actionOutcome}] ${actionNotes}`;
        if (actionOutcome === 'Start Visit') {
          newNotes = `[Start Visit] ${(isEarly ? '(Started before schedule) ' : '') + actionNotes}`;
        }

        if (!actionOutcome?.toLowerCase()?.includes('schedule') && !actionOutcome?.toLowerCase()?.includes('follow')) {
          await api.post('/followups', {
            leadId: lead.id,
            date: new Date().toISOString(),
            notes: newNotes,
            type: getFollowupType(actionOutcome, nextStep),

          });
        }
      } else {
        if (actionOutcome === 'Start Visit') {
          await api.post('/followups', {
            leadId: lead.id,
            date: new Date().toISOString(),
            notes: actionNotes,
            type: 'Visit Started',

          });
        } else if (!actionOutcome?.toLowerCase()?.includes('schedule') && !actionOutcome?.toLowerCase()?.includes('follow')) {
          await api.post('/followups', {
            leadId: lead.id,
            date: new Date().toISOString(),
            notes: actionNotes,
            type: getFollowupType(actionOutcome, nextStep),

          });
        }
      }

      const finalOutcome = (actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') ? nextStep : actionOutcome;

      if (isTerminal && !requiresDate) {
        await api.post('/followups', {
          leadId: lead.id,
          date: new Date().toISOString(),
          notes: '',
          type: getFollowupType(finalOutcome, nextStep),

        });
      }

      if (requiresDate) {
        const d = new Date(actionDate);
        const [hours, minutes] = actionTime.split(':');
        d.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        const isResched = (actionOutcome || '').toLowerCase().includes('reschedule') || (nextStep || '').toLowerCase() === 'reschedule';

        await api.post('/followups', {
          leadId: lead.id,
          date: d.toISOString(),
          notes: actionNotes || '',
          type: getFollowupType(finalOutcome, nextStep),
        });
      }

      const targetStatus = statuses.find(s => s.name.toLowerCase() === finalOutcome.toLowerCase());

      if (targetStatus) {
        const isConverted = ['won', 'completed', 'complete'].includes(targetStatus.name.toLowerCase());
        await api.put(`/leads/${lead.id}`, {
          statusId: targetStatus.id,
          isCustomer: isConverted,
          skipAutoCancel: true
        });

        if (isConverted && lead.status !== targetStatus.name) {
          const { notifyAdmins } = await import('../../utils/notify');
          await notifyAdmins(`Lead #${lead.id} (${lead.name || lead.companyName}) was converted to a customer by ${user.name}.`);
        }
      }

      toast.success("Action saved successfully");
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to save action");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 2000 }}>
      <div className="modal-content fade-in" style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '850px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
        <div className="modal-header" style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9fafb', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-text-main)' }}>Take Action</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleActionSubmit} style={{ padding: '1.5rem' }}>
          <div className="mb-4">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Action Outcome</label>
            <Dropdown
              style={{ width: '100%', borderRadius: '12px' }}
              value={actionOutcome}
              onChange={(val) => setActionOutcome(val)}
              options={dropdownOptions}
            />
          </div>

          {(actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') && (
            <div className="mb-6">
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#0369a1', fontSize: '0.9rem' }}>
                {actionOutcome === 'Complete Visit' ? 'Next Step (After Visit)' : 'Next Step (After Cancel)'}
              </label>
              <Dropdown
                style={{ width: '100%', borderRadius: '12px' }}
                value={nextStep}
                onChange={(val) => setNextStep(val)}
                options={nextStepOptions}
              />
            </div>
          )}

          {((actionOutcome?.toLowerCase()?.includes('schedule') || actionOutcome?.toLowerCase()?.includes('follow') || ((actionOutcome === 'Complete Visit' || actionOutcome === 'Cancel Visit') && (nextStep === 'Reschedule' || nextStep?.toLowerCase()?.includes('schedule')))) === true) && (
            <div className="mb-6 p-4 rounded-xl border border-blue-100 bg-blue-50/50" style={{ maxWidth: '100%' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#0369a1', fontSize: '0.9rem' }}>Follow-up In (Days) <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <input
                    type="number"
                    className="input-field w-full"
                    style={{ backgroundColor: 'white' }}
                    placeholder="e.g. 15"
                    min="0"
                    value={actionDaysToAdd}
                    onChange={(e) => handleActionDaysChange(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#0369a1', fontSize: '0.9rem' }}>Calculated Follow-up Date</label>
                  <DatePicker
                    selected={actionDate}
                    onChange={handleActionDateChange}
                    className="input-field w-full"
                    wrapperClassName="w-full"
                    style={{ backgroundColor: 'white', width: '100%' }}
                    placeholderText="Select date..."
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#0369a1', fontSize: '0.9rem' }}>Follow-up Time <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <DatePicker
                    wrapperClassName="w-full"
                    selected={getTimeDate(actionTime)}
                    onChange={(date) => {
                      if (date) {
                        const hours = date.getHours().toString().padStart(2, '0');
                        const minutes = date.getMinutes().toString().padStart(2, '0');
                        setActionTime(`${hours}:${minutes}`);
                      } else {
                        setActionTime('');
                      }
                    }}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="input-field w-full"
                    style={{ backgroundColor: 'white' }}
                    placeholderText="Select time..."
                    required
                    minTime={
                      actionDate && new Date(actionDate).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)
                        ? new Date()
                        : new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    maxTime={new Date(new Date().setHours(23, 59, 59, 999))}
                  />
                </div>
              </div>

              {/* Executive Schedule Preview */}
              {actionDate && (
                <div className="mt-4 p-3 bg-white rounded-md border" style={{ borderColor: '#bae6fd' }}>
                  <h4 className="text-sm font-semibold mb-2" style={{ color: '#0369a1' }}>Executive's Schedule for {new Date(actionDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</h4>
                  {isFetchingSchedule ? (
                    <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading schedule...</div>
                  ) : (executiveSchedule.followups?.length === 0 && executiveSchedule.deliveries?.length === 0) ? (
                    <div className="text-sm text-gray-500">No scheduled events for this day. Free to schedule!</div>
                  ) : (
                    <div className="flex flex-wrap gap-3 max-h-40 overflow-y-auto pr-2">
                      {executiveSchedule.followups?.map((f, i) => (
                        <div key={`f-${i}`} className="text-xs p-3 bg-white border border-slate-200 rounded-lg flex flex-col items-start gap-1.5 w-max shadow-sm">
                          <span className="font-medium text-slate-800">{f.lead?.companyName || f.lead?.contactPerson || 'Lead'}</span>
                          <span className="text-slate-500 font-medium">{new Date(f.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ))}
                      {executiveSchedule.deliveries?.map((d, i) => (
                        <div key={`d-${i}`} className="text-xs p-3 bg-white border border-slate-200 rounded-lg flex flex-col items-start gap-1.5 w-max shadow-sm">
                          <span className="font-medium text-slate-800">{d.title || d.partyName || 'Order'}</span>
                          <span className="text-slate-500 font-medium">{new Date(d.scheduledDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mb-6">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Notes (Optional)</label>
            <textarea
              className="input-field w-full"
              rows="3"
              placeholder="Summary of interaction..."
              value={actionNotes}
              onChange={e => setActionNotes(e.target.value)}
              style={{ resize: 'none', borderRadius: '12px' }}
            ></textarea>
          </div>
          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.25rem' }}>
            <button type="button" onClick={onClose} className="btn" style={{ padding: '0.5rem 1.5rem', border: '1px solid var(--color-border)', borderRadius: '12px', background: 'white', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1.5rem', border: 'none', borderRadius: '12px', background: 'var(--color-primary)', color: 'white', cursor: 'pointer', fontWeight: '500' }}>
              {isSubmitting && <Loader2 size={16} className="spinner-icon" />}
              {isSubmitting ? 'Saving...' : 'Save Action'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TakeActionModal;
